import { useParams } from '@hooks/useRouter';
import { between } from '@lib/utils';
import { createMemo } from '@packages/react';
import { findIndex } from 'lodash';
import React, { Fragment, useEffect, useRef } from 'react';
import { createTab, createTabs } from './common';
import { Provider } from './context';
import { DefaultTabsLayout } from './DefaultTabsLayout';

export { createTab };

export interface TabObject {
    index?: number;
    id?: number;
    name: string;
    label: string;
    component?: any;
    _mounted?: boolean;

    validation?: any;
    props?: { [key: string]: any };

    toString?: (prop?: string) => string;
}

// Tab event definition
export type TabEventHandler = (event: Event, index: number, tab: TabObject) => void;

interface TabContainerProps {
    index?: number;

    children?: any;
    component?: any;

    // Tabs list used to show & control tabs
    tabs: string[] | TabObject[];

    // Tab url, the tab should contain a `:tab` in it to be replaced
    // by currentTab.name
    // eg: /page/:tab/list
    url?: string;

    // Props passed to the url
    urlProps?: { [key: string]: any };

    // current tab
    tab?: string | TabObject;

    // Name of the property that will be on the router params
    // if your route definition it's `/page/:tab_name/list` you should pass
    // `{
    //      ...
    //      tabName: 'tab_name'
    // }`
    // Default set to `tab`
    tabName?: string;

    /**
     * Show/Hide the tab conditionally
     * @param tab
     */
    showTabs?: (tab: TabObject) => boolean;
    tabChips?: any;
    onTabOpen?: any;
    tabChipsStyle?: string;
}

const getTab = (tab: string | TabObject, defaultValue) => {
    if (typeof tab === 'string') {
        return tab;
    }

    if (!tab) {
        return defaultValue;
    }

    return tab.name;
};

export const TabContainer = createMemo<TabContainerProps>(
    (props) => {
        const api$ = useRef<any>(null);
        const {
            tabs,
            url,
            tabName = 'tab',
            tab,
            index,
            children,
            component,
            urlProps,
            showTabs,
            tabChips,
            tabChipsStyle,

            ...rest
        } = props;

        // Validate props
        validate(props);

        // Get router things
        const params = useParams([tabName]);
        const RootComponent = component || Fragment;

        /**
         * Mapping tabs, string[] will become TabObject[].
         * ['home', 'test-page'] => [
         *      { name: 'home', label: 'Home' }
         *      { name: 'test-page', label: 'Test Page' }
         * ]
         */
        const tabs$ = createTabs(tabs as any);

        // Find index of current tab & declare index state
        const tab$ = getTab(tab as any, params[tabName]);
        const index$ = typeof index !== 'undefined' ? index : findIndex(tabs$, ({ name }) => name === tab$);
        const currentIndex$ = between(index$, 0, tabs$.length - 1);

        useEffect(() => {
            if (props.onTabOpen) {
                props.onTabOpen(tab$);
            }
        }, [tab$]);

        useEffect(() => {
            // Trigger index update when user navigate with backward/forward button
            if (api$.current) {
                const [state$, { setIndex }] = api$.current;

                if (currentIndex$ !== state$.get('index')) {
                    setIndex(currentIndex$);
                }
            }
        }, [api$.current, currentIndex$]);

        useEffect(() => {
            if (api$.current) {
                api$.current[1].setShowTabs(showTabs);
            }
        }, [showTabs, api$.current]);

        useEffect(() => {
            if (api$.current) {
                api$.current[1].setTabChips(tabChips);
            }
        }, [tabChips, api$.current]);

        return (
            <Provider value={{ tabs: tabs$, index: currentIndex$, url, urlProps, tabChipsStyle }} apiRef={api$}>
                <RootComponent {...rest}>{children ? children : <DefaultTabsLayout />}</RootComponent>
            </Provider>
        );
    },
    {
        displayName: 'TabContainer',
    },
);

const validate = ({ tabs }) => {
    if (!tabs) {
        throw new Error(`Options doesn't have 'tabs' defined!
    example: {
        tabs: [
            {
                name: 'test'
                label: 'Test Tab'
            }
        ]
    }
`);
    }
};
